import Close from '../../../icons/Close'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip'

interface MultiSelectClearButtonProps {
  label: string
  onClear: () => void
}

function isDisabledByFieldset(element: HTMLSpanElement) {
  return element.closest('fieldset:disabled') !== null
}

/**
 * Inline clear affordance for a filter trigger that is itself a `<button>` — Popover and Select
 * triggers both are, and a nested button is invalid HTML, so this is a `role="button"` span whose
 * activation stops propagating rather than also opening the trigger's popover. Keyboard users can
 * reach the same action from a Clear control inside the popover.
 */
export function MultiSelectClearButton({ label, onClear }: MultiSelectClearButtonProps) {
  function handleKeyDown(event: React.KeyboardEvent<HTMLSpanElement>) {
    if (event.key !== 'Enter' && event.key !== ' ') return
    if (isDisabledByFieldset(event.currentTarget)) return

    event.preventDefault()
    event.stopPropagation()
    onClear()
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="button"
            tabIndex={0}
            aria-label={`Clear ${label}`}
            onClick={event => {
              if (isDisabledByFieldset(event.currentTarget)) return

              event.stopPropagation()
              onClear()
            }}
            onKeyDown={handleKeyDown}
            className="text-secondary-foreground hover:text-foreground focus-visible:inset-ring-ring flex size-6.5 shrink-0 cursor-pointer items-center justify-center rounded-md outline-none focus-visible:inset-ring-2"
          >
            <Close className="size-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{`Clear ${label}`}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
